import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getAIEvents } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Bot, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function AIActivity() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const data = await getAIEvents();
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load AI events');
    } finally {
      setLoading(false);
    }
  };

  const getAgentColor = (agent) => {
    switch (agent) {
      case 'triage': return 'from-purple-500 to-violet-500';
      case 'maintenance': return 'from-orange-500 to-amber-500';
      case 'arrears': return 'from-red-500 to-rose-500';
      case 'lease': return 'from-blue-500 to-cyan-500';
      default: return 'from-gray-500 to-slate-500';
    }
  };

  return (
    <Layout>
      <div data-testid="ai-activity-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">AI Activity</h1>
          <p className="text-gray-600">Monitor autonomous AI agent actions</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : events.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <Bot className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No AI activity yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <Card key={event.id} data-testid={`event-card-${event.id}`} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${getAgentColor(event.agent_name)} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                      <Bot className="w-6 h-6" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-gray-900 capitalize">{event.agent_name} Agent</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-sm text-gray-600">{event.event_type}</span>
                        {event.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 ml-auto" />
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(event.created_at), 'MMM dd, yyyy HH:mm:ss')}
                        </div>
                      </div>
                      
                      {event.error_message && (
                        <div className="mt-2 p-3 bg-red-50 rounded-lg">
                          <p className="text-sm text-red-900">Error: {event.error_message}</p>
                        </div>
                      )}
                      
                      <details className="mt-3">
                        <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-700">
                          View Details
                        </summary>
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                          <pre className="text-xs text-gray-700 overflow-auto">
                            {JSON.stringify(event.output_data, null, 2)}
                          </pre>
                        </div>
                      </details>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}